import { ReactNode, useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react'
import { useAdapter } from '../Adapter'
import { DrawerProps, Flex } from '@mantine/core'
import { Button, Popover } from '@gate/gui'
import { BaseDrawerStyled, BasePopoverTargetStyled, BaseDrawerCustomClose } from './style'
import { useClickAway, useScroll } from 'ahooks'

export interface BaseDrawerProps extends Omit<DrawerProps, 'opened' | 'onClose' | 'target'> {
  children: ReactNode
  target: ReactNode
  isButton?: boolean
  isRight?: boolean
  rightIcon?: ReactNode
  rightIconGap?: number
  isScroll?: boolean
  customClose?: boolean
  close?: boolean
}

interface BaseDrawerRef {
  onClose: () => void
}

const BaseDrawer = forwardRef<BaseDrawerRef, BaseDrawerProps>(
  (
    {
      children,
      target,
      title,
      isButton,
      isRight = true,
      rightIcon = (
        <svg className="iconpark-icon size-16 popover-icon">
          <use href="#v31-16px-line-xiajiantou"></use>
        </svg>
      ),
      rightIconGap = 8,
      isScroll = true,
      close,
      ...props
    },
    ref
  ) => {
    const { isH5 } = useAdapter()
    const [opened, setOpened] = useState(false)
    const scroll = useScroll()
    const currentRef = useRef<any>(null)

    useClickAway(() => {
      setOpened(false)
    }, currentRef)

    useEffect(() => {
      if (isScroll && scroll && scroll.top > 200) {
        if (opened) {
          setOpened(false)
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scroll])

    const onClose = () => {
      setOpened(false)
    }

    // 使用 useImperativeHandle 使父组件可以通过 ref 访问 next 和 prev 方法
    useImperativeHandle(ref, () => ({
      onClose,
    }))

    useEffect(() => {
      if (close) {
        const handleCloseButtonClick = (event: any) => {
          let target = event.target
          while (target) {
            if (target.id === 'close') {
              onClose()
              break
            }
            target = target.parentNode
          }
        }

        if (opened) {
          document.addEventListener('click', handleCloseButtonClick)
        } else {
          document.removeEventListener('click', handleCloseButtonClick)
        }

        return () => {
          document.removeEventListener('click', handleCloseButtonClick)
        }
      }
    }, [onClose, opened, close])

    return (
      <>
        {isH5 ? (
          <>
            {isButton ? (
              <Button
                ref={currentRef}
                size="sm"
                type="outlined"
                styles={{ root: { borderRadius: '4px' } }}
                rightIcon={
                  <svg className="iconpark-icon size-16 popover-icon">
                    <use href="#v31-16px-line-xiajiantou"></use>
                  </svg>
                }
                onClick={() => void setOpened(true)}
                className="cursor"
              >
                {target}
              </Button>
            ) : (
              <Flex align="center" gap={8} onClick={() => void setOpened(true)} className="cursor">
                {target}
                {isRight && (
                  <svg className="iconpark-icon size-16 popover-icon">
                    <use href="#v31-16px-line-xiajiantou"></use>
                  </svg>
                )}
              </Flex>
            )}
            <BaseDrawerStyled
              closeOnClickOutside
              position="bottom"
              styles={{
                header: {
                  padding: '4px 12px',
                  marginBottom: '0',
                },
                title: {
                  paddingTop: '8px',
                  paddingBottom: '8px',
                  fontSize: '16px',
                  fontWeight: 700,
                },
                closeButton: {
                  width: '16px',
                  height: '16px',
                },
                drawer: {
                  padding: 0,
                  minHeight: props?.size ?? '60vh',
                  borderTopLeftRadius: '12px',
                  borderTopRightRadius: '12px',
                },
              }}
              title={title}
              {...props}
              withinPortal
              opened={opened}
              onClose={() => setOpened(false)}
            >
              {props?.customClose ? (
                <BaseDrawerCustomClose>
                  <svg
                    className="iconpark-icon size-16"
                    onClick={() => {
                      setOpened(false)
                    }}
                  >
                    <use href="#v31-16px-line-guanbi-dc083ek1"></use>
                  </svg>
                </BaseDrawerCustomClose>
              ) : null}

              <div ref={currentRef}>{children}</div>
            </BaseDrawerStyled>
          </>
        ) : (
          <Popover
            opened={opened}
            onClose={() => setOpened(false)}
            closeOnClickOutside
            width="auto"
            position="bottom-end"
            offset={4}
            withinPortal
            zIndex={13}
            styles={{
              dropdown: {
                border: 0,
                borderRadius: '6px',
                background: 'var(--color-card-3)',
                boxShadow: '0px 7px 20px 0px #0D1F4212',
                minWidth: '240px',
              },
            }}
          >
            <BasePopoverTargetStyled ref={currentRef}>
              {isButton ? (
                <Button
                  size="sm"
                  type="outlined"
                  styles={{ root: { borderRadius: '4px' } }}
                  rightIcon={rightIcon}
                  onClick={() => void setOpened(true)}
                  className="cursor"
                >
                  {target}
                </Button>
              ) : (
                <Flex align="center" gap={rightIconGap} onClick={() => void setOpened(true)} className="cursor">
                  {target}
                  {isRight && rightIcon}
                </Flex>
              )}
            </BasePopoverTargetStyled>

            <Popover.Dropdown {...props}>
              <Flex ref={currentRef} py={8}>
                {children}
              </Flex>
            </Popover.Dropdown>
          </Popover>
        )}
      </>
    )
  }
)

BaseDrawer.displayName = 'BaseDrawer'

export default BaseDrawer
